import type { DatosNegocioNuevo } from '../ui/FormularioNegocio';

/**
 * Los datos del negocio entre que se capturan y que llega el código. En `localStorage`: el enlace
 * del correo abre otra pestaña. Caducan en una hora, llevan el documento del titular, y la
 * autorización de tratamiento no se guarda — se vuelve a marcar.
 */

const CLAVE = 'tanteo-alta-negocio';
const VIGENCIA_MS = 60 * 60 * 1000;

/** Lo que se conserva: el correo del alta y el negocio, sin la autorización. */
interface AltaPendiente {
  correo: string;
  datos: Omit<DatosNegocioNuevo, 'autorizaDatos'>;
}

/**
 * El alta completa —con la autorización marcada— entre que se captura y que `/crear-negocio` la
 * usa. Viaja por el contexto de la aplicación, en memoria: aquí no se guarda nada de esto.
 */
export interface AltaVerificada {
  correo: string;
  datos: DatosNegocioNuevo;
}

interface Guardado extends AltaPendiente {
  en: number;
}

/** Interpreta lo guardado: null si no hay, si no se entiende o si ya caducó. */
export function interpretarAlta(texto: string | null, ahora: number): AltaPendiente | null {
  if (!texto) return null;
  let g: Guardado;
  try {
    g = JSON.parse(texto) as Guardado;
  } catch {
    return null;
  }
  if (typeof g?.en !== 'number' || ahora - g.en > VIGENCIA_MS) return null;
  if (!g.correo || !g.datos?.nombre) return null;
  return { correo: g.correo, datos: sinAutorizacion(g.datos) };
}

/** La autorización se marca, nunca se restaura: se cae aquí y al guardar. */
function sinAutorizacion(datos: AltaPendiente['datos']): AltaPendiente['datos'] {
  const copia = { ...datos };
  delete (copia as { autorizaDatos?: boolean }).autorizaDatos;
  return copia;
}

export function guardarAlta(correo: string, datos: DatosNegocioNuevo, ahora = Date.now()): void {
  const g: Guardado = { correo, datos: sinAutorizacion(datos), en: ahora };
  try {
    localStorage.setItem(CLAVE, JSON.stringify(g));
  } catch {
    /* almacenamiento lleno o bloqueado: el alta sigue por el camino del código */
  }
}

export function leerAlta(ahora = Date.now()): AltaPendiente | null {
  try {
    return interpretarAlta(localStorage.getItem(CLAVE), ahora);
  } catch {
    return null;
  }
}

export function olvidarAlta(): void {
  try {
    localStorage.removeItem(CLAVE);
  } catch {
    /* nada que hacer: el negocio ya está creado */
  }
}
